<template>
  <DashboardContainer>
    <GivarooSpinner v-if="loading" class="mt-24" />
    <template v-else>
      <!-- Toolbar -->
      <div class="mb-5 list-toolbar">
        <BaseButton variant="primary" :icon="PlusIcon" @click="openAddModal">
          Add Fundraiser
        </BaseButton>
        <div class="list-toolbar__right">
          <span class="list-toolbar-badge">
            <span class="text-gray-400">Records</span>
            <span class="font-semibold text-gray-900">{{ fundraisers.pagination.totalCount }}</span>
          </span>
          <BaseButton
            variant="secondary"
            :icon="FunnelIcon"
            @click="showFilterDrawer = !showFilterDrawer"
          >
            Filters
            <span
              v-if="activeFilterCount"
              class="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white"
            >
              {{ activeFilterCount }}
            </span>
          </BaseButton>
        </div>
      </div>

      <!-- Inline filters -->
      <FilterDrawer
        :open="showFilterDrawer"
        title="Filter Fundraisers"
        subtitle="Refine the fundraiser list"
        @close="showFilterDrawer = false"
      >
        <div class="filter-fields">
          <FilterMultiselect
            field-label="Fundraisers"
            v-model="selectedFundraiserFilter"
            :options="fundraiserFiltersIndexes"
            :multiple="true"
            :close-on-select="false"
            placeholder="Select fundraisers"
            selectLabel="Apply"
            deselectLabel="Remove"
            label="fullName"
            track-by="id"
            clearable
          />
          <FilterMultiselect
            field-label="Home Offices"
            v-model="selectedHomeOfficeFilter"
            :options="homeOfficesFiltersIndexes"
            :multiple="true"
            :close-on-select="false"
            placeholder="Select home offices"
            selectLabel="Apply"
            deselectLabel="Remove"
            label="name"
            track-by="id"
            clearable
          />
          <div class="filter-field">
            <label class="filter-field-label">Status</label>
            <select v-model="filter" class="input--text--small w-full">
              <option value="active">Active fundraisers</option>
              <option value="inactive">Inactive fundraisers</option>
            </select>
          </div>
        </div>
        <template #footer>
          <div class="filter-drawer-footer">
            <BaseButton variant="primary" @click="applyFilters">Done</BaseButton>
          </div>
        </template>
      </FilterDrawer>

      <!-- Fundraiser list (mobile) -->
      <div class="md:hidden">
        <Spinner v-if="updatingTable" class="mt-8" />
        <template v-else>
          <ul role="list" class="space-y-3">
            <li
              v-for="(fundraiser, key) in fundraisers.list"
              :key="key"
              class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200"
            >
              <p class="truncate text-sm font-semibold text-gray-900">{{ fundraiser.fullName }}</p>
              <p class="truncate text-xs text-gray-500">{{ fundraiser.email }}</p>
              <div class="mt-2 flex items-center justify-between text-sm">
                <span class="text-gray-500">Home Office</span>
                <span class="text-gray-700">{{
                  fundraiser.homeOffice ? fundraiser.homeOffice.name : 'N/A'
                }}</span>
              </div>
              <div class="mt-3 flex gap-2">
                <BaseButton variant="secondary" size="xs" @click="toggleStatus(fundraiser)">
                  {{ fundraiser.isActive ? 'Inactivate' : 'Activate' }}
                </BaseButton>
                <BaseButton
                  variant="primary"
                  size="xs"
                  :disabled="!isOnlyFundraiser(fundraiser)"
                  v-tooltip="
                    !isOnlyFundraiser(fundraiser) ? 'Cannot edit users with additional roles' : ''
                  "
                  @click="isOnlyFundraiser(fundraiser) && openEditModal(fundraiser)"
                >
                  Update
                </BaseButton>
              </div>
            </li>
          </ul>
        </template>
      </div>

      <!-- Fundraiser list (desktop) -->
      <div class="hidden md:block">
        <BaseTable
          :columns="fundraiserColumns"
          :rows="fundraisers.list"
          :loading="updatingTable"
          empty-text="No fundraisers found."
        >
          <template #cell-name="{ row }">
            <span class="font-medium text-gray-900">{{ row.fullName }}</span>
          </template>
          <template #cell-email="{ row }">{{ row.email }}</template>
          <template #cell-homeOffice="{ row }">
            {{ row.homeOffice ? row.homeOffice.name : 'N/A' }}
          </template>
          <template #cell-status="{ row }">
            <div class="flex items-center justify-center gap-2">
              <BaseButton variant="secondary" size="xs" @click="toggleStatus(row)">
                {{ row.isActive ? 'Inactivate' : 'Activate' }}
              </BaseButton>
              <BaseButton
                variant="primary"
                size="xs"
                :disabled="!isOnlyFundraiser(row)"
                v-tooltip="!isOnlyFundraiser(row) ? 'Cannot edit users with additional roles' : ''"
                @click="isOnlyFundraiser(row) && openEditModal(row)"
              >
                Update
              </BaseButton>
            </div>
          </template>
        </BaseTable>

        <!-- Pagination -->
        <BasePagination
          v-if="fundraisers.list?.length"
          v-model="itemsPerPage"
          :page="fundraisers.pagination.page"
          :total-pages="totalPages"
          :total-count="fundraisers.pagination.totalCount"
          :display-start="displayStart"
          :display-end="displayEnd"
          :has-prev-page="fundraisers.pagination.hasPrevPage"
          :has-next-page="fundraisers.pagination.hasNextPage"
          @page-size-change="updateItemsPerPage"
          @prev="prevPage"
          @next="nextPage"
        />
      </div>
    </template>
    <Modal
      headerText="Changes have been done successfully"
      :subheaderText="fundraiserSuccessMessage"
      ctaText="Close"
      :modalOpen="successModalOpen"
      @closeModal="toggleSuccessModal"
      :showSuccessIcon="true"
    />
    <Modal
      headerText="Failed :("
      ctaText="Close and Try Again"
      :subheaderText="'Reason: ' + fundraiserFailureMessage"
      :modalOpen="failureModalOpen"
      @closeModal="toggleFailureModal"
      :showFailureIcon="true"
    />
    <!-- Add Fundraiser Modal -->
    <Modal
      headerText="Add New Fundraiser"
      subheaderText="Enter details to invite a new fundraiser."
      :modalOpen="addModalOpen"
      :showCancelButton="true"
      wide
      @cancelAction="closeAddModal"
    >
      <FundraiserFormInfo
        hide-header
        @submitForm="createFundraiser"
        @cancel="closeAddModal"
        :clearForm="clearForm"
        :initial-home-offices="homeOfficesList"
        :initial-home-offices-has-more="homeOfficesHasMore"
      />
    </Modal>
    <!-- Edit Fundraiser Modal -->
    <Modal
      headerText="Edit Fundraiser"
      subheaderText="Update fundraiser details and save your changes."
      ctaText="Save Changes"
      :modalOpen="editModalOpen"
      :showCancelButton="true"
      wide
      :disableCTA="!!employeeIdError || updating"
      @cancelAction="toggleEditModal"
      @closeModal="updateFundraiser"
    >
      <FundraiserEditForm
        :fundraiser="editingFundraiser"
        :employee-id-error="employeeIdError"
        :can-change-profile-type="canChangeProfileType"
        :has-active-campaigns="hasActiveCampaigns"
        :home-offices-list="homeOfficesList"
        :home-offices-has-more="homeOfficesHasMore"
        @remove-profile-picture="removeProfilePicture"
        @file-change="onEditFileChange"
        @validate-employee-id="validateEmployeeId"
        @update-field="updateEditingField"
      />
    </Modal>
  </DashboardContainer>
</template>

<script>
import { useStore } from 'vuex'
import { onBeforeMount, ref, computed, triggerRef, reactive, watch } from 'vue'
import DashboardContainer from '@/components/DashboardContainer.vue'
import Modal from '@/components/Modal'
import Spinner from '@/components/Spinner'
import { ArrowRightIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
import { FunnelIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { fundraiserFunctions, UserApi, fetchHomeOfficesPage } from '@/services/users/'
import FundraiserFormInfo from '@/components/FundraiserFormInfo'
import FundraiserEditForm from '@/components/FundraiserEditForm.vue'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import { base64FromFile } from '@/utils/imageHelpers'

export default {
  name: 'FundraiserList',
  components: {
    DashboardContainer,
    Modal,
    Spinner,
    ChevronRightIcon,
    ArrowRightIcon,
    FundraiserFormInfo,
    FundraiserEditForm,
    GivarooSpinner,
  },
  setup() {
    const store = useStore()
    const userToken = store.getters.user.token
    const { fundraiserCollection, fundraiserFilters } = fundraiserFunctions()
    const fundraisers = ref(fundraiserCollection)
    const loading = ref(false)
    const updatingTable = ref(false)
    const updating = ref(false)
    const successModalOpen = ref(false)
    const failureModalOpen = ref(false)
    let fundraiserFailureMessage = ref('')
    let fundraiserSuccessMessage = ref('')
    let clearForm = ref(false)
    const filter = ref('active')
    const homeOfficesList = ref([])
    const homeOfficesHasMore = ref(false)
    const totalPages = ref()
    const itemsPerPage = ref(50)
    const editModalOpen = ref(false)
    const addModalOpen = ref(false)
    const employeeIdError = ref('')
    const editingFundraiser = ref({
      id: null,
      firstName: '',
      lastName: '',
      email: '',
      homeOfficeId: null,
      employeeId: '',
      password: '',
      profilePicture: null, // null = no change, string = new/remove
      profilePicturePreview: null,
      currentProfilePicture: null,
      profileType: '',
    })

    const filterIndexes = reactive({
      fundraisersFilter: [],
      homeOfficesFilter: [],
    })
    const fundraiserFiltersIndexes = computed(() => filterIndexes.fundraisersFilter)
    const homeOfficesFiltersIndexes = computed(() => {
      return [...filterIndexes.homeOfficesFilter].sort((a, b) => {
        const nameA = (a.name || '').toLowerCase()
        const nameB = (b.name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    })
    const selectedFundraiserFilter = ref([])
    const selectedHomeOfficeFilter = ref([])

    // Modern UI: filter drawer + table columns (presentation only)
    const showFilterDrawer = ref(false)
    const activeFilterCount = computed(
      () =>
        selectedFundraiserFilter.value.length +
        selectedHomeOfficeFilter.value.length +
        (filter.value !== 'active' ? 1 : 0),
    )
    const fundraiserColumns = [
      { key: 'name', label: 'Name', minWidth: '12rem' },
      { key: 'email', label: 'Email', minWidth: '14rem' },
      { key: 'homeOffice', label: 'Home Office', align: 'center', minWidth: '10rem' },
      { key: 'status', label: 'Status', align: 'center', minWidth: '12rem' },
    ]

    const isOnlyFundraiser = (fundraiser) => {
      return (
        fundraiser.groups &&
        fundraiser.groups.length === 1 &&
        fundraiser.groups.some((g) => g.name === 'Fundraiser')
      )
    }

    const validateEmployeeId = (employeeId) => {
      const pattern = /^[A-Za-z0-9]*$/
      if (employeeId && !pattern.test(employeeId)) {
        employeeIdError.value = 'ID must only contain letters and numbers.'
        return false
      }
      employeeIdError.value = ''
      return true
    }

    const onEditFileChange = async (e) => {
      const file = e.target.files[0]
      if (!file) {
        editingFundraiser.value.profilePicture = null
        editingFundraiser.value.profilePicturePreview = null
        return
      }
      try {
        const base64 = await base64FromFile(file)
        editingFundraiser.value.profilePicture = base64
        editingFundraiser.value.profilePicturePreview = base64
      } catch (err) {
        alert('Invalid image file.')
      }
    }

    const removeProfilePicture = () => {
      editingFundraiser.value.profilePicture = '' // backend: remove
      editingFundraiser.value.profilePicturePreview = null
      editingFundraiser.value.currentProfilePicture = null // UI: hide immediately
    }

    onBeforeMount(async () => {
      loading.value = true
      fundraisers.value.pagination.size = itemsPerPage.value
      setFilters()
      await fundraisers.value.refresh().catch((error) => {
        alert(error)
      })
      totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
      triggerRef(fundraisers)
      try {
        const res = await fetchHomeOfficesPage(1)
        homeOfficesList.value = res.results || []
        homeOfficesHasMore.value = !!res.next
      } catch (error) {
        alert(error)
      }

      const response = await UserApi.csc.retrieveFilterIndexes()
      filterIndexes.fundraisersFilter = response.fundraisers || []
      filterIndexes.homeOfficesFilter = response.homeOffices || []
      loading.value = false
    })

    const openAddModal = () => {
      addModalOpen.value = true
    }

    const closeAddModal = () => {
      addModalOpen.value = false
    }

    const createFundraiser = async (fundraiserData) => {
      loading.value = true
      try {
        await UserApi.csc.inviteFundraiser(fundraiserData)
        closeAddModal()
        toggleSuccessModal()
      } catch (e) {
        console.log(e)
        const backendData = e?.response?.data
        const emailMessage = Array.isArray(backendData?.email)
          ? backendData.email[0]
          : backendData?.email
        const normalizedEmailMessage = String(emailMessage || '').toLowerCase()
        const duplicateEmailDetected =
          normalizedEmailMessage.includes('already exists') ||
          normalizedEmailMessage.includes('already been taken') ||
          normalizedEmailMessage.includes('unique')

        const backendMessage =
          typeof backendData === 'string'
            ? backendData
            : backendData?.detail ||
              emailMessage ||
              backendData?.error ||
              (Array.isArray(backendData?.non_field_errors)
                ? backendData.non_field_errors[0]
                : backendData?.non_field_errors) ||
              (backendData ? JSON.stringify(backendData) : '')

        fundraiserFailureMessage.value = duplicateEmailDetected
          ? 'This user already exists. Please use a different email address or contact admin support for assistance.'
          : backendMessage || e?.message || 'Unable to create user. Please try again.'
        toggleFailureModal()
      } finally {
        setFilters()
        await fundraisers.value.refresh().catch((error) => {
          alert(error)
        })
      }
      triggerRef(fundraisers)
      loading.value = false
    }

    const toggleSuccessModal = () => {
      successModalOpen.value = !successModalOpen.value
      !successModalOpen.value ? (clearForm.value = true) : (clearForm.value = false)
    }

    const toggleFailureModal = () => {
      failureModalOpen.value = !failureModalOpen.value
    }

    const toggleStatus = async (fundraiser) => {
      try {
        if (fundraiser.campaigns && fundraiser.campaigns.length > 0) {
          alert('Fundraiser must be removed from all campaign assignments before retiring.')
          return
        }
        const updatedStatus = !fundraiser.isActive
        const payload = {
          id: fundraiser.id,
          isActive: updatedStatus,
        }
        await UserApi.csc.customUpdate(payload)
        await fundraisers.value.refresh().catch((error) => {
          alert(error)
        })
        triggerRef(fundraisers)
      } catch (error) {
        console.error('Failed to update status', error)
        alert('Failed to update status')
      }
    }

    const applyFilters = () => {
      showFilterDrawer.value = false
    }

    const setFilters = () => {
      fundraiserFilters.userStatus = filter.value
      fundraiserFilters.fundraiser = selectedFundraiserFilter.value
        .map((fundraiser) => fundraiser.id)
        .join(',')
      fundraiserFilters.homeOffice = selectedHomeOfficeFilter.value
        .map((homeOffice) => homeOffice.id)
        .join(',')
    }
    const getFundraisers = async () => {
      updatingTable.value = true
      setFilters()
      fundraisers.value.pagination.page = 1
      fundraisers.value.pagination.size = itemsPerPage.value
      await fundraisers.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(fundraisers)
      totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
      updatingTable.value = false
    }

    const nextPage = async () => {
      if (fundraisers.value.pagination.hasNextPage) {
        updatingTable.value = true
        fundraisers.value.pagination.size = itemsPerPage.value
        await fundraisers.value.nextPage().catch((error) => {
          alert(error)
        })
        triggerRef(fundraisers)
        totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
        updatingTable.value = false
      }
    }

    const prevPage = async () => {
      if (fundraisers.value.pagination.hasPrevPage) {
        updatingTable.value = true
        fundraisers.value.pagination.size = itemsPerPage.value
        await fundraisers.value.prevPage().catch((error) => {
          alert(error)
        })
        triggerRef(fundraisers)
        totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
        updatingTable.value = false
      }
    }

    const updateItemsPerPage = async () => {
      updatingTable.value = true
      fundraisers.value.pagination.size = itemsPerPage.value
      fundraisers.value.pagination.page = 1
      setFilters()
      await fundraisers.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(fundraisers)
      totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
      updatingTable.value = false
    }

    const displayStart = computed(() => {
      return (fundraisers.value.pagination.page - 1) * itemsPerPage.value + 1
    })

    const displayEnd = computed(() => {
      const end = fundraisers.value.pagination.page * itemsPerPage.value
      return end > fundraisers.value.pagination.totalCount
        ? fundraisers.value.pagination.totalCount
        : end
    })

    const hasActiveCampaigns = computed(() => {
      if (!editingFundraiser.value.id) return false
      const fundraiser = fundraisers.value.list.find((f) => f.id === editingFundraiser.value.id)
      if (!fundraiser || !fundraiser.campaigns) return false
      return fundraiser.campaigns.length > 0
    })

    const canChangeProfileType = computed(() => {
      return (
        store.getters.isCanvassingOrgAdmin ||
        store.getters.isCanvassingOrgFieldDirector ||
        store.getters.isGivarooStaff
      )
    })

    const openEditModal = (fundraiser) => {
      // Determine current profile type from groups
      let currentProfileType = 'Fundraiser'
      if (fundraiser.groups && fundraiser.groups.length > 0) {
        const hasFieldDirector = fundraiser.groups.some(
          (g) => g.name === 'Canvassing Org Field Director',
        )
        if (hasFieldDirector) {
          currentProfileType = 'Canvassing Org Field Director'
        }
      }

      editingFundraiser.value = {
        id: fundraiser.id,
        firstName: fundraiser.firstName,
        lastName: fundraiser.lastName,
        email: fundraiser.email,
        employeeId: fundraiser.employeeId || '',
        homeOfficeId: fundraiser.homeOffice ? fundraiser.homeOffice.id : '',
        password: fundraiser.password,
        profilePicture: null,
        profilePicturePreview: null,
        currentProfilePicture: fundraiser.profilePicture || null,
        profileType: currentProfileType,
      }
      employeeIdError.value = ''
      editModalOpen.value = true
    }

    const updateEditingField = ({ key, value }) => {
      editingFundraiser.value[key] = value
    }

    const toggleEditModal = () => {
      editModalOpen.value = !editModalOpen.value
    }

    const updateFundraiser = async () => {
      try {
        if (!validateEmployeeId(editingFundraiser.value.employeeId)) {
          return
        }

        toggleEditModal()
        updating.value = true

        const payload = {
          id: editingFundraiser.value.id,
          firstName: editingFundraiser.value.firstName,
          lastName: editingFundraiser.value.lastName,
          email: editingFundraiser.value.email,
          employeeId: editingFundraiser.value.employeeId || '',
          homeOffice: editingFundraiser.value.homeOfficeId || '',
          password: editingFundraiser.value.password,
        }

        // Only include profilePicture if the user made a change
        if (editingFundraiser.value.profilePicture !== null) {
          payload.profilePicture = editingFundraiser.value.profilePicture
        }
        // null → no change (do not send)
        // '' → remove image
        // base64 string → new image

        // Include profileType if user is Canvassing Org Admin or Field Director
        if (canChangeProfileType.value && editingFundraiser.value.profileType) {
          payload.profileType = editingFundraiser.value.profileType
        }

        await UserApi.csc.customUpdate(payload)

        fundraiserSuccessMessage.value = 'Fundraiser updated successfully'
        toggleSuccessModal()
        await fundraisers.value.refresh()
      } catch (error) {
        console.error('Failed to update fundraiser', error)
        fundraiserFailureMessage.value =
          error.response?.data?.detail ||
          error.response?.data?.profile_type?.[0] ||
          error.response?.data?.email ||
          error.response?.data?.password?.join(' ') ||
          'Update failed'
        toggleFailureModal()
      } finally {
        updating.value = false
      }
    }
    watch(
      [
        () => selectedFundraiserFilter.value,
        () => selectedHomeOfficeFilter.value,
        () => filter.value,
      ],
      () => {
        setFilters()
        getFundraisers()
      },
    )

    return {
      displayStart,
      displayEnd,
      nextPage,
      prevPage,
      updateItemsPerPage,
      totalPages,
      itemsPerPage,
      filter,
      applyFilters,
      fundraisers,
      userToken,
      loading,
      updatingTable,
      successModalOpen,
      failureModalOpen,
      fundraiserFailureMessage,
      fundraiserSuccessMessage,
      clearForm,
      createFundraiser,
      toggleSuccessModal,
      toggleFailureModal,
      toggleStatus,
      editModalOpen,
      addModalOpen,
      openAddModal,
      closeAddModal,
      editingFundraiser,
      openEditModal,
      toggleEditModal,
      updateEditingField,
      updateFundraiser,
      isOnlyFundraiser,
      employeeIdError,
      validateEmployeeId,
      selectedFundraiserFilter,
      selectedHomeOfficeFilter,
      fundraiserFiltersIndexes,
      homeOfficesFiltersIndexes,
      updating,
      onEditFileChange,
      removeProfilePicture,
      hasActiveCampaigns,
      canChangeProfileType,
      store,
      homeOfficesList,
      homeOfficesHasMore,
      showFilterDrawer,
      activeFilterCount,
      fundraiserColumns,
      FunnelIcon,
      PlusIcon,
    }
  },
}
</script>
